import type { CollectionStrings } from "../collection-strings";

/**
 * English — the wording the collections shipped with, moved here verbatim so
 * the translations have a reference and nothing was lost in the extraction.
 *
 * Two descriptions were deliberately shortened on the way in, because they were
 * written for a developer reading the file rather than an editor reading a form
 * hint, and the `StatusSwitchField` labels beside them now say the operational
 * half:
 *
 * - `news.published` was a 263-character paragraph covering develop-vs-production
 *   build semantics and Firestore readability. The build behaviour is what the
 *   toggle's own labels state; the embargo warning belongs in onboarding and in
 *   invariant 6 of the spec, not re-read on every save.
 * - `news.tags` dropped its explanation of what happens to a deleted tag, which
 *   is a property of the read path rather than something an editor decides.
 */
export const en: CollectionStrings = {
    article: {
        newArticle: {
            button: "New article",
            failed:
                "Could not create the article. Nothing was saved — try again, and check you are still signed in.",
        },
        retiredTags: (names) =>
            `Cannot save — these tags are retired: ${names.join(", ")}. A retired tag renders nowhere on the site: no chip, no filter link, no page. Remove them from this article, or un-retire them under Tags first.`,
        fields: {
            date: {
                name: "Publish date",
                description: "ISO format: 2026-09-02. Prefilled with today.",
            },
            updatedAt: {
                name: "Last updated",
                description:
                    "Maintained automatically. Feeds the sitemap's lastmod, so a hand-set value would be a fabricated signal.",
            },
            slug: {
                name: "Slug",
                description:
                    "The URL segment, shared by both locales: /news/<slug>. Numbered automatically when you create the article, and PERMANENT — older articles keep their written slug.",
            },
            published: {
                name: "Published",
                description:
                    "Off until someone says otherwise. A draft is visible on the develop preview only.",
                onLabel: "Live on the site",
                offLabel: "Draft — develop preview only",
            },
            tags: {
                name: "Tags",
                description: "Optional. Retired tags are not offered.",
            },
            product: {
                name: "Product",
                description:
                    "Optional. The product this article is about. Leave blank for company news.",
            },
            img: {
                name: "Hero image",
                description:
                    "Optional. Upload a file or paste a URL. Used on the detail page and as og:image — the listing rows carry no image.",
            },
            title: { name: "Title" },
            description: {
                name: "Description",
                description:
                    "Optional. The meta description, and the two lines under the title in the /news list. Written, not derived from the body — an empty one ships no description at all.",
            },
            body: {
                name: "Body",
                description: "Markdown. Both languages are required.",
            },
        },
    },
    tag: {
        newTag: {
            button: "New tag",
            failed:
                "Could not create the tag. Nothing was saved — try again, and check you are still signed in.",
        },
        fields: {
            date: {
                name: "Created",
                description: "ISO format: 2026-09-02. Prefilled with today.",
            },
            label: {
                name: "Label",
                description:
                    "The chip text. This is CONTENT, not UI copy — it does not go through the repo's translation review.",
            },
            category: {
                name: "Category tag",
                description:
                    "Shows this tag as a filter pill on the news page. Off, the tag still has its own page and still appears as a chip on its articles \u2014 it is just not offered as a filter.",
                onLabel: "Shown as a filter",
                offLabel: "Not a filter",
            },
            retired: {
                name: "Retired",
                description:
                    "Hides the tag everywhere public: no chip, no filter link, no page. Articles keep the slug and the label stays recoverable.",
                onLabel: "Hidden from the site",
                offLabel: "Active",
            },
            order: {
                name: "Order",
                description:
                    "Optional. Sets the filter-bar order; oldest tag first when left blank.",
            },
        },
    },
    streams: {
        blog: {
            article: {
                collection: {
                    name: "Blog",
                    singularName: "Article",
                    description:
                        "Articles published at one-ai's /blog. Each owns its page. This is the content that lived at /news until 2026-09-16 and at /notes until 2026-09-17; its Firestore path is still `notes`.",
                },
                invalidSlug: (slug) =>
                    `Article ID "${slug}" is not valid. Use lowercase letters, digits and hyphens only — no spaces, capitals or underscores. It becomes the permanent URL /blog/${slug} and cannot be changed later.`,
            },
            tag: {
                collection: {
                    name: "Blog tags",
                    singularName: "Blog tag",
                    description:
                        "Tags for the One AI blog. Editors may add freely.",
                },
                invalidSlug: (slug) =>
                    `Tag slug "${slug}" is not valid. Use lowercase letters, digits and hyphens only — no spaces, capitals or underscores. It becomes the permanent URL /blog/tag/${slug} and cannot be changed later.`,
            },
        },
        news: {
            article: {
                collection: {
                    name: "News",
                    singularName: "Article",
                    description:
                        "Articles published at one-ai's /news. Each owns its page. Created 2026-09-16 and starts empty.",
                },
                invalidSlug: (slug) =>
                    `Article ID "${slug}" is not valid. Use lowercase letters, digits and hyphens only — no spaces, capitals or underscores. It becomes the permanent URL /news/${slug} and cannot be changed later.`,
            },
            tag: {
                collection: {
                    name: "News tags",
                    singularName: "News tag",
                    description:
                        "Tags for One AI news articles. Editors may add freely.",
                },
                invalidSlug: (slug) =>
                    `Tag slug "${slug}" is not valid. Use lowercase letters, digits and hyphens only — no spaces, capitals or underscores. It becomes the permanent URL /news/tag/${slug} and cannot be changed later.`,
            },
        },
    },
    announcements: {
        collection: {
            name: "Announcements",
            singularName: "Announcement",
            description: "Drives the NEWS section on the OneLive home page.",
        },
        fields: {
            date: { name: "Date", description: "ISO format: 2026-05-22" },
            type: { name: "Category" },
            title: { name: "Title" },
            url: { name: "Link" },
        },
    },
    cases: {
        collection: {
            name: "Cases",
            singularName: "Case",
            description: "Drives the card grid on the OneLive /case page.",
        },
        fields: {
            date: { name: "Date", description: "ISO format: 2026-06-17" },
            title: { name: "Title" },
            description: { name: "Description" },
            img: { name: "Hero image URL" },
            url: { name: "Article link" },
        },
    },
    tagPicker: {
        edit: "Choose tags",
        empty: "No tags",
        remove: (name) => `Remove ${name}`,
    },
    upload: {
        button: "Upload",
        uploading: (percent) => `Uploading… ${percent}%`,
        placeholder: "https://…",
        preview: "Hero image preview",
        errors: {
            tooLarge: (limitMb) =>
                `That image is too large. The limit is ${limitMb} MB.`,
            notAnImage: "That file is not an image.",
            notConfigured:
                "Uploads are not configured — VITE_UPLOAD_ENDPOINT is unset. Paste a URL instead.",
            notAllowed:
                "Your session was rejected by the upload service. Sign out and back in, then try again.",
            generic: "The upload failed. Try again, or paste a URL instead.",
        },
    },
    richText: {
        placeholder: "Write the article…",
        toolbar: {
            paragraph: "Body text",
            heading2: "Heading",
            heading3: "Subheading",
            bold: "Bold",
            italic: "Italic",
            underline: "Underline",
            strikethrough: "Strikethrough",
            inlineCode: "Code",
            bulletList: "Bulleted list",
            numberedList: "Numbered list",
            quote: "Quote",
            codeBlock: "Code block",
            link: "Link",
            image: "Insert image",
            embed: "Insert video",
            undo: "Undo",
            redo: "Redo",
        },
        link: {
            prompt: "Link URL",
            invalid:
                "Use an https:// address, a mailto: address, or a path starting with /. Other schemes are removed when the site is built.",
        },
        image: {
            alt: "Alt text",
            altPlaceholder: "What the image shows, for screen readers",
            remove: "Remove",
        },
        embed: {
            prompt: "YouTube or Vimeo URL",
            unsupported:
                "Only YouTube and Vimeo links can be embedded. Anything else is removed when the site is built.",
            remove: "Remove",
        },
        upload: {
            button: "Upload",
            uploading: (percent) => `Uploading… ${percent}%`,
            placeholder: "https://…",
            preview: "Body image preview",
            errors: {
                tooLarge: (limitMb) =>
                    `That image is too large. The limit is ${limitMb} MB.`,
                notAnImage: "That file is not an image.",
                notConfigured:
                    "Uploads are not configured — VITE_UPLOAD_ENDPOINT is unset.",
                notAllowed:
                    "Your session was rejected by the upload service. Sign out and back in, then try again.",
                generic: "The upload failed. Try again.",
            },
        },
    },
    rebuild: {
        button: (environment) => `Rebuild ${environment}`,
        tooltip: (environment) =>
            `Rebuild the ${environment} site from the current records`,
        triggered: (environments) =>
            `Rebuilding ${environments}. The change appears once the build finishes — usually a minute or two.`,
        allFailed: (environments) =>
            `Could not trigger the ${environments} rebuild. The record IS saved — only the rebuild failed, so use the Rebuild button to retry.`,
        partial: (ok, failed) =>
            `Saved, and ${ok} is rebuilding, but the ${failed} rebuild could not be triggered. Use the Rebuild button to retry it.`,
    },
};
